import { useWalletAuth } from '@/hooks/useWalletAuth'
import { formatNumber } from '@ourzora/utils/dist'
import { usePostHog } from 'posthog-js/react'
import { Button, Image, Modal, Text, Theme, useToggle, View } from 'reshaped'
import { CopyToClipboardButton } from '../Share/CopyToClipboardButton'
import { SocialShareButtons } from '../Share/SocialShareButtons'
import styles from '../Splash/Splash.module.css'
import { SplashVideo } from '../Splash/SplashVideo'
import { AnimatedNumber } from './AnimatedNumber'

function ShareModal({
  active,
  deactivate,
  claimedAmount,
}: {
  active: boolean
  deactivate: () => void
  claimedAmount: number
}) {
  const claimUrl = `https://claim.zora.co`
  const shareMessage = `Just claimed ${formatNumber(
    Math.floor(claimedAmount)
  )} $ZORA on @zora`

  return (
    <Theme name="zora-ui">
      <Modal
        active={active}
        onClose={deactivate}
        size={{ l: '460px' }}
        position={{ s: 'bottom', l: 'center' }}
      >
        <View gap={6} padding={2}>
          <View direction="column" align="center" gap={2}>
            <Text variant="featured-3" weight="semibold" color="primary">
              Share to social
            </Text>

            <Text variant="body-3" color="primary">
              Let your friends know you claimed
            </Text>
          </View>

          <Image
            src="/assets/og-image.jpeg"
            width="100%"
            alt="Share image"
            borderRadius="large"
          />

          <View gap={{ s: 2, m: 3 }} direction="row">
            <SocialShareButtons message={shareMessage} shareUrl={claimUrl} />
            <View.Item grow>
              <CopyToClipboardButton value={claimUrl} />
            </View.Item>
          </View>
        </View>
      </Modal>
    </Theme>
  )
}

interface ClaimSuccessProps {
  totalClaimed: number
}

export function ClaimSuccess({ totalClaimed }: ClaimSuccessProps) {
  const { login } = useWalletAuth()
  const postHog = usePostHog()
  const {
    active: isShareModalOpen,
    activate: openShareModal,
    deactivate: closeShareModal,
  } = useToggle(false)

  const handleConnectAnotherWallet = () => {
    // Track connect another wallet event
    postHog.capture('connect_another_wallet_clicked')
    login()
  }

  const handleShareButtonClick = () => {
    // Track share event
    postHog.capture('claim_share_clicked', {
      totalClaimed,
    })

    // Open the share modal (original functionality)
    openShareModal()
  }

  return (
    <View width="100%">
      <View
        position="fixed"
        insetTop={0}
        insetStart={0}
        insetEnd={0}
        insetBottom={0}
        width="100vw"
        height="100vh"
        zIndex={1}
        attributes={{
          style: {
            pointerEvents: 'none',
          },
        }}
      >
        <SplashVideo />
      </View>

      <View zIndex={2}>
        <View direction="column" align="center" gap={8} padding={4}>
          <View
            position="relative"
            align="center"
            justify="center"
            height="200px"
            width={{ s: '100%', m: '925px' }}
          >
            <AnimatedNumber
              number={formatNumber(Math.floor(totalClaimed))}
              totalClaimableLength={totalClaimed}
            />
          </View>

          <View position="relative" width="100%" maxWidth="700px" paddingBlock={6}>
            <View
              position="absolute"
              height="100%"
              width="100%"
              zIndex={3}
              borderRadius="xlarge"
              backgroundColor="page"
              insetTop={0}
              insetStart={0}
              attributes={{
                style: {
                  boxShadow:
                    '0px 0px 40px 0px #FFF, 0px 0px 20px 0px #FFF, 0px 0px 20px 0px #FFF, 0px 0px 20px 0px #FFF, 0px 0px 40px 0px #FFF, 0px 0px 20px 0px #FFF, 0px 0px 100px 0px #FFF',
                  backdropFilter: 'blur(0.5px)',
                },
              }}
            />
            <View
              position="relative"
              zIndex={4}
              align="center"
              paddingBlock={4}
              direction="column"
              gap={4}
            >
              <Text variant="featured-2" weight="regular" color="primary">
                You successfully claimed $ZORA
              </Text>

              <Button
                variant="solid"
                color="primary"
                size="xlarge"
                onClick={handleShareButtonClick}
                attributes={{
                  style: {
                    border: '2px solid #D2D2D2',
                    borderRadius: '12px',
                    height: '60px',
                    width: '388px',
                    maxWidth: '100%',
                    background:
                      'linear-gradient(180deg, #0085FF 0%, #000AFF 50%, #0085FF 100%)',
                    pointerEvents: 'auto',
                    position: 'relative',
                    zIndex: 2,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                  },
                }}
              >
                <Text
                  variant="featured-3"
                  weight="semibold"
                  attributes={{
                    style: {
                      color: 'white',
                    },
                  }}
                >
                  Share
                </Text>
              </Button>
              <Button
                variant="solid"
                color="primary"
                size="xlarge"
                onClick={handleConnectAnotherWallet}
                className={styles.metallicButton}
                attributes={{
                  style: {
                    pointerEvents: 'auto',
                    height: '60px',
                  },
                }}
              >
                <Text variant="featured-3" weight="semibold" color="primary">
                  Try another wallet
                </Text>
              </Button>
              <View paddingTop={4} zIndex={4}>
                <Text variant="body-2" weight="regular" color="primary">
                  <a
                    href="https://support.zora.co/en/articles/5626113"
                    rel="noopener noreferrer"
                  >
                    How do I access my $ZORA?
                  </a>
                </Text>
              </View>
            </View>
          </View>
        </View>
      </View>

      <ShareModal
        active={isShareModalOpen}
        deactivate={closeShareModal}
        claimedAmount={totalClaimed}
      />
    </View>
  )
}
